import * as React from "react";
import {
    useContext,
    createContext,
    useState,
    useCallback,
    type Dispatch,
    type SetStateAction,
    type PropsWithChildren,
} from "react";

import { noop } from "../../../../../../../Common/TypingUtils";

const DEFAULT_VISIBLE_CHILDREN_COUNT = 6;
const SHOW_MORE_CHILDREN_STEP = 10;

type VisibleChildrenCountCollection = Record<string, number>;
type SetVisibleChildrenCountCallback = Dispatch<SetStateAction<VisibleChildrenCountCollection>>;

const VisibleChildrenCountContext = createContext<VisibleChildrenCountCollection>({});
const SetVisibleChildrenCountContext = createContext<SetVisibleChildrenCountCallback>(noop);

export const useShowMoreChildren = (elementId: string): (() => void) => {
    const setCollection = useContext(SetVisibleChildrenCountContext);
    const visibleChildrenCount = useVisibleChildrenCount(elementId);

    const setVisibleChildrenCount = useCallback(() => {
        setCollection(prev => ({ ...prev, [elementId]: visibleChildrenCount + SHOW_MORE_CHILDREN_STEP }));
    }, [elementId, visibleChildrenCount, setCollection]);

    return setVisibleChildrenCount;
};

export const useVisibleChildrenCount = (elementId: string): number => {
    const visibleChildrenCountCollection = useContext(VisibleChildrenCountContext);
    const visibleChildrenCount = visibleChildrenCountCollection[elementId];

    return visibleChildrenCount ?? DEFAULT_VISIBLE_CHILDREN_COUNT;
};

export const useRevertShowMoreChildren = (elementId: string): (() => void) => {
    const setCollection = useContext(SetVisibleChildrenCountContext);
    const visibleChildrenCount = useVisibleChildrenCount(elementId);

    const revert = useCallback(() => {
        setCollection(prev => ({ ...prev, [elementId]: visibleChildrenCount - SHOW_MORE_CHILDREN_STEP }));
    }, [elementId, visibleChildrenCount, setCollection]);

    return revert;
};

export const useResetVisibleChildrenCount = (): (() => void) => {
    const setCollection = useContext(SetVisibleChildrenCountContext);

    const reset = useCallback(() => {
        setCollection({});
    }, []);

    return reset;
};

export const VisibleChildrenCountProvider = ({ children }: PropsWithChildren<{}>) => {
    const [collection, setCollection] = useState<VisibleChildrenCountCollection>({});

    return (
        <SetVisibleChildrenCountContext.Provider value={setCollection}>
            <VisibleChildrenCountContext.Provider value={collection}>{children}</VisibleChildrenCountContext.Provider>;
        </SetVisibleChildrenCountContext.Provider>
    );
};
